export type CommunicationQuestionType = 'choice' | 'ox' | 'short' | 'essay';

/** OX형 고정 선택지 (응답 body_text 는 'O' / 'X') */
export const COMMUNICATION_OX_CHOICES: readonly CommunicationChoiceOption[] = [
  { id: 'o', text: 'O' },
  { id: 'x', text: 'X' },
];

/** 선택지로 응답하는 유형(선택형·OX형) — 집계·학생 블럭·정답 판정 공통 */
export function isChoiceLikeCommunicationQuestion(q: CommunicationQuestion | null | undefined): boolean {
  return q?.type === 'choice' || q?.type === 'ox';
}

/** 선택지 번호 배지 파스텔 (교사 모달·학생 QR 공통) */
/** 보기 번호 배지 색 (질문 페이지 기준, 리모트·학생 화면 공통) */
export function communicationChoiceColor(index: number): string {
  const n = COMMUNICATION_CHOICE_COLORS.length;
  return COMMUNICATION_CHOICE_COLORS[((index % n) + n) % n]!;
}

/** 보기 배지 인라인 스타일: `.cp-choice-swatch` 와 함께 사용 (App.css) */
export function communicationChoiceSwatchStyle(index: number): Record<string, string> {
  return { '--cp-swatch': communicationChoiceColor(index) };
}

/** OX 배지 색 (App.css :root 의 --cp-ox-* 공통 변수) */
export function communicationOxBadgeStyle(isO: boolean): { background: string; color: string } {
  return isO
    ? { background: 'var(--cp-ox-o-bg)', color: 'var(--cp-ox-o)' }
    : { background: 'var(--cp-ox-x-bg)', color: 'var(--cp-ox-x)' };
}

export const COMMUNICATION_CHOICE_COLORS = [
  '#f8c4d4',
  '#f7d5b8',
  '#cfe8c8',
  '#c5daf5',
  '#e4d4f5',
  '#f5e6b8',
] as const;

export type CommunicationChoiceOption = {
  id: string;
  text: string;
};

export type CommunicationQuestion = {
  type: CommunicationQuestionType;
  question: string;
  choices?: CommunicationChoiceOption[];
  /** 선택형: 0-based 정답 인덱스 (OX형: 0=O, 1=X, 최대 1개) */
  correctChoiceIndexes?: number[];
  /** 단답형 정답 목록 */
  answers?: string[];
  /** 서술형 안내 */
  guide?: string;
};

export function emptyCommunicationQuestion(
  type: CommunicationQuestionType = 'short'
): CommunicationQuestion {
  if (type === 'choice') {
    return {
      type: 'choice',
      question: '',
      choices: [
        { id: 'c1', text: '' },
        { id: 'c2', text: '' },
        { id: 'c3', text: '' },
        { id: 'c4', text: '' },
      ],
      correctChoiceIndexes: [],
    };
  }
  if (type === 'ox') {
    return { type: 'ox', question: '', choices: COMMUNICATION_OX_CHOICES.map((c) => ({ ...c })), correctChoiceIndexes: [] };
  }
  if (type === 'essay') {
    return { type: 'essay', question: '', guide: '' };
  }
  return { type: 'short', question: '', answers: [''] };
}

export function parseCommunicationQuestion(raw: unknown): CommunicationQuestion | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const type = o.type;
  if (type !== 'choice' && type !== 'ox' && type !== 'short' && type !== 'essay') return null;
  const question = String(o.question ?? '');
  if (type === 'ox') {
    const idxRaw = Array.isArray(o.correctChoiceIndexes) ? o.correctChoiceIndexes : [];
    const first = idxRaw.map((n) => Number(n)).find((n) => n === 0 || n === 1);
    return {
      type,
      question,
      choices: COMMUNICATION_OX_CHOICES.map((c) => ({ ...c })),
      correctChoiceIndexes: first != null ? [first] : [],
    };
  }
  if (type === 'choice') {
    const choicesRaw = Array.isArray(o.choices) ? o.choices : [];
    const choices = choicesRaw
      .map((c, i) => {
        if (!c || typeof c !== 'object') return null;
        const row = c as Record<string, unknown>;
        const id = String(row.id ?? `c${i + 1}`).trim() || `c${i + 1}`;
        return { id, text: String(row.text ?? '') };
      })
      .filter((c): c is CommunicationChoiceOption => c != null);
    const idxRaw = Array.isArray(o.correctChoiceIndexes) ? o.correctChoiceIndexes : [];
    const correctChoiceIndexes = idxRaw
      .map((n) => Number(n))
      .filter((n) => Number.isFinite(n) && n >= 0)
      .map((n) => Math.trunc(n));
    return { type, question, choices, correctChoiceIndexes };
  }
  if (type === 'short') {
    const answersRaw = Array.isArray(o.answers) ? o.answers : [];
    const answers = answersRaw.map((a) => String(a ?? '').trim()).filter(Boolean);
    return { type, question, answers: answers.length ? answers : [''] };
  }
  return { type, question, guide: String(o.guide ?? '') };
}

/** 슬라이드 팁·레거시 answer_text 동기화용 */
export function communicationQuestionToAnswerText(q: CommunicationQuestion): string {
  if (q.type === 'short') {
    return (q.answers ?? [])
      .map((a) => a.trim())
      .filter(Boolean)
      .map((a) => (a.startsWith('#') ? a : `#${a}`))
      .join(' ');
  }
  if (q.type === 'ox') {
    const i = (q.correctChoiceIndexes ?? [])[0];
    return i === 0 ? '#O' : i === 1 ? '#X' : '';
  }
  if (q.type === 'choice') {
    const choices = q.choices ?? [];
    const idxs = q.correctChoiceIndexes ?? [];
    return idxs
      .map((i) => {
        const text = (choices[i]?.text ?? '').trim();
        if (!text) return '';
        return `#${i + 1}번 ${text}`;
      })
      .filter(Boolean)
      .join(' ');
  }
  return '';
}

export function communicationQuestionTipLabels(q: CommunicationQuestion | null): string[] {
  if (!q) return [];
  if (q.type === 'short') {
    return (q.answers ?? []).map((a) => a.trim()).filter(Boolean);
  }
  if (q.type === 'ox') {
    const i = (q.correctChoiceIndexes ?? [])[0];
    return i === 0 ? ['O'] : i === 1 ? ['X'] : [];
  }
  if (q.type === 'choice') {
    const choices = q.choices ?? [];
    return (q.correctChoiceIndexes ?? [])
      .map((i) => {
        const text = (choices[i]?.text ?? '').trim();
        return text ? `${i + 1}번 ${text}` : '';
      })
      .filter(Boolean);
  }
  return [];
}

function normalizeCompare(value: string): string {
  return String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

/** 선택지 표시 라벨 (텍스트 없으면 N번) */
export function communicationChoiceOptionLabel(
  choice: CommunicationChoiceOption | undefined,
  index: number
): string {
  const text = (choice?.text ?? '').trim();
  return text || `${index + 1}번`;
}

function uniqueSortedIndexes(indexes: number[], maxExclusive: number): number[] {
  const out: number[] = [];
  for (const raw of indexes) {
    if (!Number.isFinite(raw)) continue;
    const i = Math.trunc(raw);
    if (i < 0 || i >= maxExclusive) continue;
    if (!out.includes(i)) out.push(i);
  }
  return out.sort((a, b) => a - b);
}

/** 복수 선택 → body_text (줄바꿈 구분) */
export function formatCommunicationChoiceSelection(
  choices: CommunicationChoiceOption[],
  indexes: number[]
): string {
  return uniqueSortedIndexes(indexes, choices.length)
    .map((i) => communicationChoiceOptionLabel(choices[i], i))
    .join('\n');
}

/** body_text → 선택된 선택지 인덱스 (복수 선택·레거시 단일 선택) */
export function parseCommunicationChoiceSelection(
  bodyText: string,
  choices: CommunicationChoiceOption[]
): number[] {
  const body = String(bodyText ?? '').trim();
  if (!body || choices.length === 0) return [];

  const tokens = body.includes('\n')
    ? body
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean)
    : [body];

  const selected: number[] = [];
  for (const token of tokens) {
    const nt = normalizeCompare(token);
    for (let i = 0; i < choices.length; i++) {
      if (selected.includes(i)) continue;
      const text = (choices[i]?.text ?? '').trim();
      const label = communicationChoiceOptionLabel(choices[i], i);
      const numLabel = `${i + 1}번`;
      if (
        normalizeCompare(label) === nt ||
        (text && normalizeCompare(text) === nt) ||
        normalizeCompare(numLabel) === nt ||
        normalizeCompare(String(i + 1)) === nt ||
        (text && normalizeCompare(`${numLabel} ${text}`) === nt)
      ) {
        selected.push(i);
        break;
      }
    }
  }
  return selected.sort((a, b) => a - b);
}

/**
 * 서술형 → 채점 없음(null)
 * 정답 미등록 → 응답 있으면 정답
 * 단답 → 정답 목록 중 하나와 일치
 * 선택 → 정답 인덱스 집합과 선택 집합이 동일해야 함 (복수 선택)
 */
export function evaluateCommunicationQuestionCorrectness(
  bodyText: string,
  question: CommunicationQuestion | null,
  legacyAnswerText = ''
): boolean | null {
  const body = String(bodyText ?? '').trim();
  if (!body) return null;

  if (!question) {
    const tips = legacyAnswerText
      .split('#')
      .map((p) => p.trim())
      .filter(Boolean);
    if (tips.length === 0) return true;
    const nb = normalizeCompare(body);
    return tips.some((t) => normalizeCompare(t) === nb);
  }

  if (question.type === 'essay') return null;

  if (question.type === 'short') {
    const answers = (question.answers ?? []).map((a) => a.trim()).filter(Boolean);
    if (answers.length === 0) return true;
    const nb = normalizeCompare(body);
    return answers.some((a) => normalizeCompare(a) === nb);
  }

  /* 선택형·OX형 (OX형은 선택지 고정) */
  const choices = question.type === 'ox' ? COMMUNICATION_OX_CHOICES.slice() : question.choices ?? [];
  const idxs = uniqueSortedIndexes(question.correctChoiceIndexes ?? [], choices.length);
  if (idxs.length === 0) return true;
  const selected = parseCommunicationChoiceSelection(body, choices);
  return (
    selected.length === idxs.length && idxs.every((i, pos) => selected[pos] === i)
  );
}

/** @deprecated use evaluateCommunicationQuestionCorrectness */
export function parseCommunicationAnswers(raw: string): string[] {
  const text = String(raw ?? '').trim();
  if (!text) return [];
  const fromHash = text
    .split('#')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => part.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
  if (fromHash.length > 0) return Array.from(new Set(fromHash));
  return Array.from(
    new Set(
      text
        .split(/[,，\n]/)
        .map((part) => part.replace(/\s+/g, ' ').trim())
        .filter(Boolean)
    )
  );
}

/** @deprecated */
export function evaluateCommunicationCorrectness(
  bodyText: string,
  answerText: string
): boolean | null {
  return evaluateCommunicationQuestionCorrectness(bodyText, null, answerText);
}
